import { Menu as MenuIcon, Search, HelpCircle, Settings, Sun, Moon, Monitor, LogOut, MessagesSquare } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Avatar, IconButton, Kbd, Menu, MenuContent, MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuTrigger } from "@dfws/ui";
import type { ThemePreference } from "@dfws/contracts";
import { updateSettings, useSettings } from "../settings/store";
import { useLogout } from "../lib/queries";
import { totalUnread, useChannels } from "../features/chat/chatData";
import { setPanelOpen, useDock } from "../features/chat/dock";
import s from "./TopBar.module.css";

export function TopBar({ onToggleSidebar, onOpenPalette, onOpenHelp, email, showChat }: { onToggleSidebar: () => void; onOpenPalette: (query?: string) => void; onOpenHelp: () => void; email: string; showChat: boolean }) {
  const { theme } = useSettings();
  const unreadChat = totalUnread(useChannels().data?.items);
  const { panelOpen } = useDock();
  const navigate = useNavigate();
  const logout = useLogout();
  const name = displayName(email);
  return (
    <header className={s.bar}>
      <IconButton label="Main menu" shortcut="[" icon={<MenuIcon />} onClick={onToggleSidebar} />
      <button type="button" className={s.search} onClick={() => onOpenPalette()} aria-keyshortcuts="/ Control+K">
        <Search aria-hidden />
        <span className={s.placeholder}>Search mail, people and commands</span>
        <span className={s.keys} aria-hidden>
          <Kbd>Ctrl</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      <div className={s.right}>
        {showChat && (
          <span className={s.chatButton}>
            <IconButton label={unreadChat ? `Chat, ${unreadChat} unread` : "Chat"} icon={<MessagesSquare />} pressed={panelOpen} onClick={() => setPanelOpen(!panelOpen)} />
            {unreadChat > 0 && <span className={s.chatBadge} aria-hidden>{unreadChat > 99 ? "99+" : unreadChat}</span>}
          </span>
        )}
        <IconButton label="Keyboard shortcuts" shortcut="?" icon={<HelpCircle />} onClick={onOpenHelp} className={s.hideNarrow} />
        <IconButton label="Settings" icon={<Settings />} onClick={() => navigate({ to: "/settings" })} className={s.hideNarrow} />
        <Menu>
          <MenuTrigger asChild>
            <button type="button" className={s.account} aria-label={`Account: ${email}`}>
              <Avatar name={name} email={email} size={32} />
            </button>
          </MenuTrigger>
          <MenuContent>
            <div className={s.who}>
              <Avatar name={name} email={email} size={40} />
              <div>
                <div className={s.whoName}>{name}</div>
                <div className={s.whoEmail}>{email}</div>
              </div>
            </div>
            <MenuSeparator />
            <MenuLabel>Theme</MenuLabel>
            <MenuRadioGroup value={theme} onValueChange={(v) => updateSettings({ theme: v as ThemePreference })}>
              <MenuRadioItem value="light" icon={<Sun />}>Light</MenuRadioItem>
              <MenuRadioItem value="dark" icon={<Moon />}>Dark</MenuRadioItem>
              <MenuRadioItem value="system" icon={<Monitor />}>Match system</MenuRadioItem>
            </MenuRadioGroup>
            <MenuSeparator />
            <MenuItem icon={<Settings />} onSelect={() => navigate({ to: "/settings" })}>All settings</MenuItem>
            <MenuItem
              icon={<LogOut />}
              onSelect={() =>
                logout.mutate(undefined, {
                  onSettled: () => void navigate({ to: "/login" }),
                })
              }
            >
              Sign out
            </MenuItem>
          </MenuContent>
        </Menu>
      </div>
    </header>
  );
}

/** "ahtesham.ahmed@devfusion.tech" -> "Ahtesham Ahmed" until the profile name is known. */
function displayName(email: string): string {
  const local = email.split("@")[0] ?? "";
  return local.split(/[._-]+/).filter(Boolean).map((p) => p[0]!.toUpperCase() + p.slice(1)).join(" ") || email;
}
